import { z } from "zod";
import { EnumPromotionAvailabilityType } from "gql/generated";
import type { TFunction } from "i18next";
import { isLexicalHtmlEmpty } from "shared/components/molecules/TextEditor/TextEditor";

export type PromotionFormValues = {
  listingEntityId: string;
  title: string;
  description: string;
  link: string;
  availabilityType: EnumPromotionAvailabilityType;
  dates: string[];
  startDate: string;
  endDate: string;
  imagesToKeep: string[];
  organizationTags: string[];
};

export const PROMOTION_FORM_DEFAULTS: PromotionFormValues = {
  listingEntityId: "",
  title: "",
  description: "",
  link: "",
  availabilityType: EnumPromotionAvailabilityType.SpecificDates,
  dates: [""],
  startDate: "",
  endDate: "",
  imagesToKeep: [],
  organizationTags: [],
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

const normalizeUrl = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

export const isValidPromotionLink = (value: string) => {
  if (!value.trim()) return true;
  try {
    const url = new URL(normalizeUrl(value));
    return !!url.hostname && url.hostname.includes(".");
  } catch {
    return false;
  }
};

/**
 * Builds the zod schema with translated messages. `requireListing` is true
 * for organization users creating a promotion (they must pick the listing).
 */
export const buildPromotionFormSchema = (
  t: TFunction<"events", "APP.PROMOTION_MODAL">,
  requireListing: boolean,
) =>
  z
    .object({
      listingEntityId: z.string(),
      title: z.string().trim().min(1, t("VALIDATION.TITLE_REQUIRED")),
      description: z
        .string()
        .refine(
          value => !isLexicalHtmlEmpty(value),
          t("VALIDATION.DESCRIPTION_REQUIRED"),
        ),
      link: z
        .string()
        .refine(isValidPromotionLink, t("VALIDATION.LINK_INVALID")),
      availabilityType: z.nativeEnum(EnumPromotionAvailabilityType),
      dates: z.array(z.string()),
      startDate: z.string(),
      endDate: z.string(),
      imagesToKeep: z.array(z.string()),
      organizationTags: z.array(z.string()),
    })
    .superRefine((values, ctx) => {
      if (requireListing && !values.listingEntityId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["listingEntityId"],
          message: t("VALIDATION.LISTING_REQUIRED"),
        });
      }
      if (
        values.availabilityType === EnumPromotionAvailabilityType.SpecificDates
      ) {
        const dates = values.dates.filter(date => DATE_RE.test(date));
        if (dates.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["dates"],
            message: t("VALIDATION.DATES_REQUIRED"),
          });
        }
      }
      if (values.availabilityType === EnumPromotionAvailabilityType.DateRange) {
        if (!DATE_RE.test(values.startDate) || !DATE_RE.test(values.endDate)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["startDate"],
            message: t("VALIDATION.RANGE_REQUIRED"),
          });
        } else if (values.startDate > values.endDate) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["endDate"],
            message: t("VALIDATION.RANGE_ORDER"),
          });
        }
      }
    });

/** The availability block to send to the API, with the inactive fields cleared. */
export const toAvailabilityInput = (values: PromotionFormValues) => {
  switch (values.availabilityType) {
    case EnumPromotionAvailabilityType.SpecificDates:
      return {
        type: values.availabilityType,
        dates: Array.from(
          new Set(values.dates.filter(date => DATE_RE.test(date))),
        ).sort(),
      };
    case EnumPromotionAvailabilityType.DateRange:
      return {
        type: values.availabilityType,
        dates: [],
        startDate: values.startDate,
        endDate: values.endDate,
      };
    default:
      return { type: EnumPromotionAvailabilityType.Ongoing, dates: [] };
  }
};

export const toLinkInput = (value: string) => normalizeUrl(value) || null;
